import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { adminApi } from '../service/adminApiService';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const DepartmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { department_name: paramName } = useParams<{ department_name?: string }>();
  const isEdit = Boolean(paramName);

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !paramName) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await adminApi.getDepartment(paramName);
        if (cancelled) return;
        setName(existing.department_name);
        setDisplayName(existing.display_name || '');
        setDescription(existing.description || '');
        setContactEmail(existing.contact_email || '');
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load department');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, paramName]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        department_name: name,
        display_name: displayName || name,
        description,
        contact_email: contactEmail,
      };
      if (isEdit && paramName) {
        await adminApi.updateDepartment(paramName, payload);
      } else {
        await adminApi.createDepartment(payload);
      }
      toast.push({
        type: 'success',
        message: isEdit ? `Updated ${name}` : `Created ${name}`,
      });
      navigate('/admin/departments');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Row alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Row>
    );
  }

  return (
    <Column spacing="large">
      <PageHeader
        title={isEdit ? `Edit department: ${paramName}` : 'New department'}
        subtitle="Departments attach tenant metadata and populate the group form dropdown."
      />

      {error && <Alert type="error">{error}</Alert>}

      <Section>
      <Column spacing="medium" maxWidth={720}>

      <Column spacing="xsmall">
        <label htmlFor="dept-name">Canonical name</label>
        <Input
          id="dept-name"
          type="text"
          value={name}
          onChange={setName}
          placeholder="e.g. Twitch"
          disabled={isEdit}
        />
      </Column>

      <Column spacing="xsmall">
        <label htmlFor="dept-display">Display name</label>
        <Input
          id="dept-display"
          type="text"
          value={displayName}
          onChange={setDisplayName}
          placeholder="Shown in the group form dropdown"
        />
      </Column>

      <Column spacing="xsmall">
        <label htmlFor="dept-description">Description</label>
        <Input
          id="dept-description"
          type="text"
          value={description}
          onChange={setDescription}
        />
      </Column>

      <Column spacing="xsmall">
        <label htmlFor="dept-email">Contact email</label>
        <Input
          id="dept-email"
          type="text"
          value={contactEmail}
          onChange={setContactEmail}
          placeholder="team-alias@amazon.com"
        />
      </Column>

      <Row alignmentHorizontal="end" spacing="small">
        <Button type="tertiary" onClick={() => navigate('/admin/departments')}>
          Cancel
        </Button>
        <Button type="primary" onClick={save} disabled={!name || saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create department'}
        </Button>
      </Row>
      </Column>
      </Section>
    </Column>
  );
};
